// File: src/pages/seller/AddProduct/sections/PricingEstimation.jsx
import React, { useState } from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import {
  CRow,
  CCol,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CButton,
} from "@coreui/react";
import {
  formatWithCommasAndDecimals,
  parseRawNumber,
} from "../../../../utils/price";

const PricingEstimation = ({ calculatePrice, subCategories = [] }) => {
  const {
    control,
    register,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();

  const estimated = useWatch({ control, name: "estimated_price" });
  const pricePerUnit = useWatch({ control, name: "price_per_unit" });

  const calcUnit = useWatch({ control, name: "calc_unit" });
  const currency = useWatch({ control, name: "currency" });

  const getDynamicLabel = (baseLabel, showPricePerUnit = false) => {
    if (!calcUnit || !currency) return baseLabel;
    const ratePart =
      showPricePerUnit && pricePerUnit
        ? `Price per unit: ${formatWithCommasAndDecimals(pricePerUnit)} `
        : "";
    return (
      <>
        {baseLabel}
        <span
          className="text-muted ms-2"
          style={{ fontWeight: "400", fontSize: "0.85rem" }}
        >
          ({ratePart}
          {currency}/{calcUnit})
        </span>
      </>
    );
  };

  // console.log('Subcategories in PricingEstimation:', subCategories);

  const handleEstimate = async () => {
    console.log("Button Clicked");
    const vals = getValues();
    if (!vals.subcategory_id || !vals.calc_weight || !vals.calc_unit) return;

    const subcategoryName =
      subCategories.find((s) => String(s.id) === String(vals.subcategory_id))
        ?.name || "";

    if (!subcategoryName) {
      console.error("No subcategory name found for id:", vals.subcategory_id);
      // alert("Please select a valid subcategory before calculating price.");
      return;
    }

    const payload = {
      metal: subcategoryName.toLowerCase(),
      weight: vals.calc_weight,
      unit: vals.calc_unit.toLowerCase(),
      currency: vals.currency,
    };

    console.log("Payload being sent:", payload);

    try {
      const result = await calculatePrice(payload);
      console.log("Price calculation result:", result);
      console.log("Price calculation result:", result);

      setValue("estimated_price", result.estimated_price ?? "");
      setValue("price_per_unit", result.price_per_unit ?? "");
    } catch (err) {
      console.error("Calculate price failed:", err);
      // alert(err.payload || "Unable to calculate price for this subcategory.");
      setValue("estimated_price", "");
      setValue("price_per_unit", "");
    }
  };

  return (
    <div
      style={{
        border: "1px solid #dcdcdc",
        borderRadius: "8px",
        padding: "1.5rem",
        position: "relative",
        marginBottom: "1.5rem",
      }}
    >
      {/* Section Title */}
      <span
        style={{
          position: "absolute",
          top: "-0.8rem",
          left: "1rem",
          backgroundColor: "white",
          padding: "0 0.5rem",
          fontWeight: "600",
          color: "#333",
          fontSize: "1rem",
        }}
      >
        Pricing & Estimation
      </span>

      <CRow className="mb-3 align-items-end">
        {/* Weight */}
        <CCol md={2}>
          <CFormLabel>Weight</CFormLabel>
          <CFormInput type="number" {...register("calc_weight")} />
          {errors.calc_weight && (
            <div style={{ color: "red", marginTop: "4px" }}>
              {errors.calc_weight.message}
            </div>
          )}
        </CCol>

        {/* Unit Type */}
        <CCol md={2}>
          <CFormLabel>Unit</CFormLabel>
          <Controller
            name="calc_unit"
            control={control}
            render={({ field }) => (
              <CFormSelect
                {...field}
                options={[
                  { label: "Select", value: "" },
                  { label: "Troy Ounce", value: "toz" },
                  { label: "Metric Tonne", value: "mt" },
                  { label: "Kg", value: "kg" },
                  { label: "gram", value: "g" },
                ]}
              />
            )}
          />
          {errors.calc_unit && (
            <div style={{ color: "red", marginTop: "4px" }}>
              {errors.calc_unit.message}
            </div>
          )}
        </CCol>

        {/* Currency */}
        <CCol md={2}>
          <CFormLabel>Currency</CFormLabel>
          <Controller
            name="currency"
            control={control}
            render={({ field }) => (
              <CFormSelect
                {...register("currency")}
                // invalid={!!errors.currency}
              >
                <option value="">Select Currency</option>
                <option value="AUD">AUD</option>
                <option value="USD">USD</option>
                {/* { label: "INR", value: "INR" },
                  // { label: "GBP", value: "GBP" }, // United Kingdom
                  // { label: "CNY", value: "CNY" }, // China
                  // { label: "JPY", value: "JPY" }, // Japan
                  // { label: "AED", value: "AED" }, // Dubai / UAE */}
              </CFormSelect>
            )}
          />
           {errors.currency && (
            <div style={{ color: "red", marginTop: "4px" }}>
              {errors.currency.message}
            </div>
          )}
        </CCol>

        {/* Calculate Button */}
        <CCol md={2}>
          <CButton type="button" color="info" onClick={handleEstimate}>
            Calculate
          </CButton>
        </CCol>

        {/* Estimated Price */}
        <CCol md={2}>
          <CFormLabel>{getDynamicLabel("Estimated Price")}</CFormLabel>
          <CFormInput
            value={
              estimated
                ? Number(estimated).toLocaleString(undefined, {
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3,
                  })
                : ""
            }
            readOnly
          />
        </CCol>

        {/* Price Per Unit */}
        <CCol md={2}>
          <CFormLabel>{getDynamicLabel("Price per Unit")}</CFormLabel>
          <CFormInput
            value={
              pricePerUnit
                ? Number(pricePerUnit).toLocaleString(undefined, {
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3,
                  })
                : ""
            }
            readOnly
          />
        </CCol>

        {/* Empty space for alignment */}
        <CRow className="mb-3">
          <CCol md={8}>
            <CFormLabel>{getDynamicLabel("Price", true)}</CFormLabel>
            <Controller
              name="price"
              control={control}
              render={({ field }) => {
                // Format value with commas but allow empty string
                const formattedValue = field.value
                  ? formatWithCommasAndDecimals(field.value)
                  : "";

                return (
                  <CFormInput
                    {...field}
                    value={formattedValue}
                    onChange={(e) => {
                      // Remove commas before storing in form state
                      const raw = parseRawNumber(e.target.value);
                      field.onChange(raw);
                    }}
                    placeholder="Enter price"
                  />
                );
              }}
            />
            {errors.price && (
              <div style={{ color: "red", marginTop: "4px" }}>
                {errors.price.message}
              </div>
            )}
          </CCol>
        </CRow>
      </CRow>
    </div>
  );
};

export default React.memo(PricingEstimation);
